import { PropsWithChildren, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import PageHeader from '../pageHeader/PageHeader';
import SpecialOffersBenefits from '../specialOffersBenefits/SpecialOffersBenefits';
import SpecialOffersCountdown from '../specialOffersCountdown/SpecialOffersCountdown';
import SpecialOffersDiscount from '../specialOffersDiscount/SpecialOffersDiscount';
import SpecialOffersEmptyPlaceholder from '../specialOffersEmptyPlaceholder/SpecialOffersEmptyPlaceholder';
import SpecialOffersLoader from '../specialOffersLoader/SpecialOffersLoader';

const benefits = [
  'Royalties from NFT Sale',
  'Royalties from Community',
  'Future DAO governance',
  'Promoted in Landlord Go',
  'Promoted in Major marketplaces',
];

interface ISpecialOffersLayoutProps extends PropsWithChildren {
  title: string;
  subtitle: string;
  description?: string;
  text: string;
  dataLoading: boolean;
  dataAvailable: boolean;
  dataNotAvailable: boolean;
  endTimestamp: number;
  className?: string;
}

const SpecialOffersLayout = ({
  title,
  subtitle,
  description,
  children,
  text,
  dataLoading,
  dataAvailable,
  dataNotAvailable,
  endTimestamp,
  className,
}: ISpecialOffersLayoutProps) => {
  const [state, setState] = useState({ dataLoading: true });

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (dataAvailable || dataNotAvailable) {
      timeout = setTimeout(() => setState({ dataLoading }), 500);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [dataAvailable, dataLoading, dataNotAvailable]);

  return (
    <div className="tw-mb-7.5 tw-mt-2.5 md:tw-my-0 md:tw-px-4 xl:tw-px-0">
      <PageHeader className="tw-mb-2.5 tw-mt-0 tw-px-5 tw-text-center tw-text-[32px]/[34px] lg:tw-mb-3.5 lg:tw-text-[48px]">
        {title}
      </PageHeader>
      <p className="tw-mx-auto tw-mb-7.5 tw-px-5 tw-text-center tw-text-base tw-text-white md:tw-w-3/4 lg:tw-mb-10 lg:tw-w-4/5 lg:tw-text-lg">
        {subtitle}
      </p>
      {description && (
        <p className="tw-mx-auto -tw-mt-4 tw-mb-7.5 tw-px-5 tw-text-center tw-text-base tw-text-white md:tw-w-3/4 lg:tw-mb-10 lg:tw-w-4/5 lg:tw-text-lg">
          {description}
        </p>
      )}
      <div className="tw-flex tw-items-center tw-justify-center tw-bg-backgroundColorMedium tw-pb-8 tw-text-white md:tw-mb-14 md:tw-rounded-2xl md:tw-bg-backgroundColorMedium md:tw-px-7.5 md:tw-py-12">
        {state.dataLoading ? (
          <SpecialOffersLoader />
        ) : (
          <>
            {dataNotAvailable && <SpecialOffersEmptyPlaceholder />}
            {dataAvailable && (
              <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-7.5 md:tw-flex-row md:tw-justify-between xl:tw-gap-8 2xl:tw-gap-15">
                <div
                  className={twMerge(
                    'tw-top-5 tw-mx-auto tw-flex tw-w-fit tw-min-w-[320px] tw-flex-col tw-items-center tw-self-start md:tw-sticky',
                    className
                  )}
                >
                  <SpecialOffersCountdown endTimestamp={endTimestamp} />
                  <SpecialOffersDiscount>{text}</SpecialOffersDiscount>
                  <SpecialOffersBenefits benefits={benefits} />
                </div>
                {children}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SpecialOffersLayout;
